import { getFlavorRatios } from './strainFlavorRatios';

// Base flavor colors - optimized for MAXIMUM perceptual distinction
// Colors spread across hue spectrum with high saturation and varied lightness
const baseFlavorColors = {
  'Berry': { r: 220, g: 20, b: 60, hsl: 'hsl(348, 83%, 47%)' },      // Crimson red
  'Blueberry': { r: 65, g: 105, b: 225, hsl: 'hsl(219, 79%, 66%)' },// Royal blue  
  'Butter': { r: 255, g: 215, b: 0, hsl: 'hsl(50, 100%, 50%)' },    // Gold
  'Cheese': { r: 255, g: 140, b: 0, hsl: 'hsl(33, 100%, 50%)' },    // Dark orange
  'Citrus': { r: 50, g: 205, b: 50, hsl: 'hsl(120, 61%, 50%)' },    // Lime green
  'Diesel': { r: 0, g: 191, b: 255, hsl: 'hsl(195, 100%, 50%)' },   // Deep sky blue
  'Earthy': { r: 139, g: 69, b: 19, hsl: 'hsl(25, 76%, 31%)' },     // Saddle brown
  'Flowery': { r: 255, g: 105, b: 180, hsl: 'hsl(330, 100%, 71%)' },// Hot pink
  'Grape': { r: 138, g: 43, b: 226, hsl: 'hsl(271, 76%, 53%)' },    // Blue violet
  'Lemon': { r: 255, g: 255, b: 102, hsl: 'hsl(60, 100%, 70%)' },   // Bright yellow
  'Mint': { r: 152, g: 251, b: 152, hsl: 'hsl(120, 93%, 79%)' },    // Pale green
  'Orange': { r: 255, g: 69, b: 0, hsl: 'hsl(16, 100%, 50%)' },     // Orange red
  'Pine': { r: 34, g: 139, b: 34, hsl: 'hsl(120, 61%, 34%)' },      // Forest green
  'Pungent': { r: 148, g: 0, b: 211, hsl: 'hsl(282, 100%, 41%)' },  // Dark violet
  'Skunk': { r: 154, g: 205, b: 50, hsl: 'hsl(75, 63%, 50%)' },     // Yellow green
  'Spicy/Herbal': { r: 210, g: 105, b: 30, hsl: 'hsl(25, 75%, 47%)' }, // Chocolate
  'Sweet': { r: 255, g: 20, b: 147, hsl: 'hsl(328, 100%, 54%)' },   // Deep pink
  'Tropical': { r: 255, g: 165, b: 0, hsl: 'hsl(39, 100%, 50%)' },  // Orange
  'Vanilla': { r: 245, g: 245, b: 220, hsl: 'hsl(60, 56%, 91%)' },  // Beige
  'Woody': { r: 101, g: 67, b: 33, hsl: 'hsl(30, 51%, 26%)' },      // Dark brown
};

// Flavor to emoji/icon mapping
const flavorIcons = {
  'Berry': '🫐',
  'Butter': '🧈',
  'Cheese': '🧀',
  'Citrus': '🍋',
  'Diesel': '⛽',
  'Earthy': '🌍',
  'Grape': '🍇',
  'Lemon': '🍋',
  'Orange': '🍊',
  'Pine': '🌲',
  'Pungent': '👃',
  'Skunk': '🦨',
  'Sweet': '🍯',
  'Vanilla': '🍦',
  'Woody': '🪵',
};

// Mix colors from multiple flavors
export const mixFlavorColors = (flavors) => {
  if (!flavors || flavors.length === 0) return 'rgb(200, 200, 200)';
  
  let r = 0, g = 0, b = 0;
  let count = 0;
  
  flavors.forEach(flavor => {
    if (baseFlavorColors[flavor]) {
      const color = baseFlavorColors[flavor];
      r += color.r;
      g += color.g;
      b += color.b;
      count++;
    }
  });
  
  if (count === 0) return 'rgb(200, 200, 200)';
  
  r = Math.round(r / count);
  g = Math.round(g / count);
  b = Math.round(b / count);
  
  return `rgb(${r}, ${g}, ${b})`;
};

// Get flavor icons for display
export const getFlavorIcon = (flavor) => {
  return flavorIcons[flavor] || '🌿';
};

// Get all base flavors from entire tree
export const getBaseFlavorSet = (strainTree) => {
  const flavors = new Set();
  
  const traverse = (node) => {
    if (node.publicFlavors) {
      node.publicFlavors.forEach(f => flavors.add(f));
    }
    if (node.parents) {
      Object.values(node.parents).forEach(parent => traverse(parent));
    }
  };
  
  traverse(strainTree);
  return Array.from(flavors);
};

// Get top flavors for a strain with vote data if available.
// Priority:
//   1. strainFlavorRatios flat model (authoritative, real vote proportions)
//   2. Embedded flavor objects in strainTree.json (votes + proportion)
//   3. publicFlavors string array (equal-weight fallback)
export const getStrainFlavors = (strain) => {
  // 1. Flat model lookup — keyed by strain.id (slug)
  if (strain.id) {
    const ratios = getFlavorRatios(strain.id);
    if (ratios && ratios.length > 0) {
      return ratios;
    }
  }

  // 2. Embedded flavor objects (votes > 0 required to be salient)
  if (strain.flavors && Array.isArray(strain.flavors) && strain.flavors.length > 0) {
    const salient = strain.flavors.filter(
      f => typeof f === 'object' && (f.votes === undefined || f.votes > 0)
    );
    if (salient.length > 0) return salient;
  }

  // 3. publicFlavors string array (equal-weight fallback)
  return strain.publicFlavors || [];
};

// Get mixed flavor name based on ingredients
export const getMixedFlavorName = (flavors) => {
  if (!flavors || flavors.length === 0) return 'Neutral';
  if (flavors.length === 1) return flavors[0];
  if (flavors.length === 2) return `${flavors[0]}-${flavors[1]}`;
  return flavors.slice(0, 3).join('-');
};

// Export individual items
export { baseFlavorColors, flavorIcons };

export default {
  mixFlavorColors,
  getFlavorIcon,
  getBaseFlavorSet,
  getStrainFlavors,
  getMixedFlavorName,
  baseFlavorColors,
  flavorIcons
};
